export interface UserPermission {
    id: number;
    roleId: number;
    code: string;
    name: string;
    active: boolean;
    description: string;
    readAccess: boolean;
    writeAccess: boolean;
    groupId: number;
}

export interface PermissionGroup {
    id: number;
    name: string;
}

export interface GroupedUserPermissions {
    group: PermissionGroup;
    data: UserPermission[];
}


/**
 * Enum containing all permission codes in the system
 * Organized by permission groups for better readability
 */
export enum PermissionCode {
    // Orders/Fulfilment Group
    ACCEPT_ORDERS = 'accept_orders',
    EDIT_ORDERS = 'edit_orders',
    CANCEL_CONSIGNMENT = 'cancel_consignment',
    APPROVE_CREDIT = 'approve_credit',
    ADDITIONAL_SHIPPING_COSTS = 'additional_shipping_costs',
    SPLIT_IN_STOCK_MANUFACTURE = 'split_in_stock_manufacture',
    SPLIT_PRODUCT_REMOVAL = 'split_product_removal',
    SPLIT_RENTAL_DATES = 'split_rental_dates',
    SPLIT_PROCEED_NEXT = 'split_proceed_next',
    MANUFACTURING = 'manufacturing',
    PREPARING_SHIPPING_DETAILS = 'preparing_shipping_details',
    PREPARING_CONFIRM_READY = 'preparing_confirm_ready',
    READY_SHIPPING_DETAILS = 'ready_shipping_details',
    READY_DISPATCH_DATE = 'ready_dispatch_date',
    READY_CONFIRM_DISPATCH = 'ready_confirm_dispatch',
    RENTAL_MODIFY = 'rental_modify_addons',
    RENTAL_FORCE_OVERRIDE = 'rental_force_override',
    CUSTOMER_MODIFICATIONS = 'customer_modifications',
    RENTAL_RETURN_MODIFY_DATES = 'rental_return_modify_dates',
    RENTAL_RETURN_MODIFY_ADDONS = 'rental_return_modify_addons',
    RENTAL_RETURN_CONFIRM = 'rental_return_confirm',
    PAYMENT_RESET_RETRY = 'payment_reset_retry',
    MANUFACTURING_DELAY_NEW_DATE = 'manufacturing_delay_new_date',
    MANUFACTURING_DELAY_CONFIRM = 'manufacturing_delay_confirm',
    PICKED_UP_DETAILS_MANAGEMENT = 'picked_up_details_management',
    PICK_UP_DETAILS_MANAGEMENT = 'pickup_details_management',
    ORDER_SHIPPED_PAID_CREDIT = 'order_shipped_add_credit',
    OVERRIDE_APPROVAL = 'override_approval',

    // Product Management Group
    PRODUCTS_ON_PRODUCT_PAGE = 'products_on_product_page',
    PRODUCT_IMAGES = 'product_images',
    PRODUCT_VIDEOS = 'product_videos',
    PRODUCT_TITLE = 'product_title',
    PRODUCT_DESCRIPTION = 'product_description',
    PRODUCT_CATEGORY_SUBCATEGORY = 'category_subcategory',
    PRODUCT_BRAND = 'brand',
    PRODUCT_LEAD_DAYS = 'lead_days',
    PRODUCT_KEYWORDS = 'keywords',
    PRODUCT_VENDOR_SKU_ERP_MPN = 'vendor_sku_erp_mpn',
    PRODUCT_LOGISTICS_OPTIONS = 'logistics_options',
    PRODUCT_RENTAL_CONFIGURATION = 'rental_configuration',
    PRODUCT_SUPPORTING_DOCUMENTS = 'supporting_documents',
    PRODUCT_WAREHOUSES_ADD = 'warehouses_add',
    PRODUCT_WAREHOUSES_CREATE = 'warehouses_create',
    PRODUCT_RENT_RESTRICTED_PROVINCES = 'rent_restricted_provinces',
    PRODUCT_VARIANTS_DELETE = 'variants_delete',
    PRODUCT_VARIANTS_PURCHASE_RENT = 'variants_purchase_rent',
    PRODUCT_EXPORT_XLSX = 'export_xlsx',
    PRODUCT_IMPORT_XLSX = 'import_xlsx',
    PRODUCT_MANUALLY_EDIT_PRODUCTS = 'manually_edit_products',
    PRODUCT_EDIT_ATTRIBUTES = 'edit_attributes',
    PRODUCT_SUBMIT = 'submit',
    PRODUCT_PUBLISH = 'publish',
    PRODUCT_GO_LIVE = 'go_live',
    PRODUCT_CREATE_DELETE = 'product_create_delete',

    // Add-ons Group
    MAP_ADDONS_PRODUCTS = 'map_addons_products',
    ADDONS_CREATE = 'addons_create',
    ADDONS_PUBLISH = 'addons_publish',
    EDIT_ADDON_DETAILS = 'edit_addon_details',

    // Brands Group
    BRANDS_VIEW_CREATE_DELETE = 'brands_view_create_delete',
    BRANDS_UPDATE_LOGO = 'brands_upload_brand_logo',
    BRANDS_ADD_DETAILS = 'brands_add_details',
    BRANDS_ADD_SEO = 'brands_add_seo',

    // Categories Group
    ADD_CATEGORY = 'add_category',
    MANAGE_CATEGORY = 'manage_category',
    ADD_MANAGE_SUBCATEGORY = 'add_subcategory',

    // Attributes Group
    ATTRIBUTES_CREATE = 'attributes_create',
    ATTRIBUTES_EDIT = 'attributes_edit',
    ATTRIBUTES_MERGE = 'attributes_merge',
    ATTRIBUTES_RESET = 'attributes_reset',
    ATTRIBUTES_IMPORT = 'attributes_import',

    // Warehouses Group
    ADD_WAREHOUSE = 'add_warehouse',
    EDIT_WAREHOUSE = 'edit_warehouse',
    DELETE_WAREHOUSE = 'delete_warehouse',

    // Pricebooks Group
    VIEW_PRICEBOOKS = 'view_pricebooks',
    NEW_PRICEBOOKS = 'new_pricebooks',

    // Reports Group
    ORDER_INFORMATION = 'order_information',
    VENDOR_OVERVIEW = 'vendor_overview',
    PURCHASE_PLAN = 'purchase_plan',
    RATINGS_REVIEWS = 'ratings_reviews',
    STOCK_INFORMATION = 'stock_information',
    TRANSACTION_JOURNAL = 'transaction_journal',
    VENDOR_INSIGHTS = 'vendor_insights',
    REVENUE_RECOGNITION = 'revenue_recognition',
    VENDOR_OFMP_WEB_REPORT = 'vendor_ofmp_web_report',
    OFMP_WEB_REPORT = 'ofmp_web_report',    

    // Marketing Group
    MARKETING_CAMPAIGNS = 'marketing_campaigns',
    ANALYTICS_DASHBOARD = 'analytics_dashboard',

    // Settings Group
    EMAIL_REMINDERS = 'email_reminders',
    PAYMENT_REMINDERS = 'payment_reminders',
    AUTOMATION_SETTINGS = 'automation_settings',

    // Users/Organization Group
    INVITE_BUSINESS_UNIT = 'invite_business_unit',
    INVITE_VENDOR_ADMINS = 'invite_vendor_admins',
    ORGANIZATION_EDIT_USERS = 'organization_edit_users',
    ORGANIZATION_REMOVE_USERS = 'organization_remove_users',
    ORGANIZATION_REMOVE_ADMINS = 'organization_remove_admins',
    CREATE_BUSINESS_UNIT = 'create_business_unit',
    EDIT_REMOVE_BUSINESS_UNIT = 'edit_remove_business_unit',

    // Affiliate Group
    AFFILIATE_MANAGEMENT = 'affiliate_program_setup',
    AFFILIATE_COMISSION_MANAGEMENT = 'affiliate_commission_manage',
    APPROVE_COMMISSION = 'approve_commission',
    UNAPPROVE_COMMISSION = 'unapprove_commission',
    PAY_COMMISSION = 'pay_commission',
}